import axios from 'axios'
import config from '../Config'
import { HTML_LANG } from '../i18n'

/* Instapres v1 API (pres repo: api/v1.py, docs/API_V1.md). Every call
   resolves to the response body or throws; callers show their own error
   state. ?lang= is on every request because caches key on the URL only. */
const client = axios.create({ baseURL: `${config.API_URL}/v1`, timeout: 15000 })

const followedParam = (followed) =>
  followed?.length ? { followedSources: followed.join(',') } : {}

async function get(path, params = {}, options = {}) {
  const response = await client.get(path, { ...options, params: { ...params, lang: HTML_LANG } })
  return response.data
}

async function post(path, body, params = {}, options = {}) {
  const response = await client.post(path, body, {
    ...options,
    params: { ...params, lang: HTML_LANG },
  })
  return response.data
}

const authHeaders = (token) => (token ? { headers: { Authorization: `Bearer ${token}` } } : {})

const enc = encodeURIComponent

/* Feed page parameters. `refresh` = the reader asked for the newest (the
   pill, tap to refresh): a unique value gets past HTTP caches and makes the
   API recompute the page. */
const pageParams = ({ sort, cursor, followed, refresh }) => ({
  ...(sort ? { sort } : {}),
  ...(cursor ? { cursor } : {}),
  ...followedParam(followed),
  ...(refresh ? { refresh: Date.now() } : {}),
})

export const api = {
  feed: ({ category, sort, ...page }) =>
    get('/feed', {
      ...(category ? { category } : {}),
      ...pageParams({ ...page, sort: sort === 'new' ? 'new' : null }),
    }),

  newCount: ({ category, sort, since, snapshot }) =>
    get('/feed/new-count', {
      ...(category ? { category } : {}),
      ...(sort === 'new' ? { sort } : {}),
      since,
      ...(snapshot ? { snapshot } : {}),
    }),

  story: (id, { followed } = {}) => get(`/stories/${enc(id)}`, followedParam(followed)),

  trending: ({ limit = 15, exclude, category } = {}) =>
    get('/topics/trending', {
      limit,
      ...(exclude?.length ? { exclude: exclude.join(',') } : {}),
      ...(category ? { category } : {}),
    }),

  topic: (slug) => get(`/topics/${enc(slug)}`),
  topicStories: (slug, page) => get(`/topics/${enc(slug)}/stories`, pageParams(page)),

  sources: () => get('/sources'),
  source: (slug) => get(`/sources/${enc(slug)}`),
  sourceStories: (slug, page) => get(`/sources/${enc(slug)}/stories`, pageParams(page)),

  categories: () => get('/categories'),
  category: (slug) => get(`/categories/${enc(slug)}`),
  categoryStories: (slug, page) => get(`/categories/${enc(slug)}/stories`, pageParams(page)),

  search: (q) => get('/search', { q }),
  searchStories: (q, page) => get('/search/stories', { q, ...pageParams(page) }),

  suggest: (q) => get('/topics/suggest', { q }),

  myTopicsFeed: (topics, { cursor, followed, refresh }) =>
    post('/my-topics/feed', {
      topics,
      cursor: cursor || null,
      followedSources: followed || [],
      ...(refresh ? { refresh: true } : {}),
    }),

  // Fire and forget; sendBeacon survives the tab switching to the article.
  articleOpened: (articleId) => {
    if (!articleId) return
    const url = `${config.API_URL}/v1/articles/${articleId}/open`
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url)) return
    } catch (e) {
      // fall through
    }
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
  },

  // Which optional features the server has switched on ({ discussions }).
  config: () => get('/config'),

  /* Comments on stories (Komentari, Diskusije). Threads are personal
     ("mine"), so they get past every cache with a unique `_`. */
  discussions: ({ limit, ...page } = {}) =>
    get('/discussions', { ...(limit ? { limit } : {}), ...pageParams(page) }),
  storyComments: (id, token) =>
    get(`/stories/${enc(id)}/comments`, { _: Date.now() }, authHeaders(token)),
  postComment: (id, { articleId, name, body }, token) =>
    post(`/stories/${enc(id)}/comments`, { articleId, name, body }, {}, authHeaders(token)),
  deleteComment: (commentId, token) =>
    client.delete(`/comments/${commentId}`, authHeaders(token)),
  reportComment: (commentId, token) =>
    post(`/comments/${commentId}/report`, null, {}, authHeaders(token)),
  // Anonymous profile (the same one the apps use for "Moje teme"): a token, no account.
  createProfile: async () => {
    const response = await axios.post(`${config.API_URL}/me`, {}, { timeout: 15000 })
    return response.data.token
  },

  // One-time move of the old server-side "Moje teme" profile to this browser.
  legacyMyTopics: async (token) => {
    const response = await axios.get(`${config.API_URL}/me/topics`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { _: Date.now() },
      timeout: 15000,
    })
    return response.data
  },
}
